import {
    Injectable,
    OnDestroy
} from "@angular/core";

import {
    fromEvent,
    Subject,
    timer
} from "rxjs";

import {
    delay,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";

import {
    BarcodeManagerService
} from "src/app/core";

import {
    EposFiscalDevice
} from "./epos-fiscal-device.constants";

interface PrinterConnection {
    ipAddress: string;
    buffer: string[];
    socket: WebSocket;
    shouldReconnect: boolean;
    destroy$: Subject<void>;
    resetBuffer$: Subject<void>;
}

/**
 * Service to manage WebSocket connections with Epson Fiscal Printers
 * specifically for handling integrated barcode scanner input.
 */
@Injectable({
    providedIn: 'root'
})
export class EposFiscalDeviceService implements OnDestroy {
    /** Interval for sending ping messages to keep the WebSocket alive (30s) */
    private readonly HEARTBEAT_INTERVAL = 30000;
    /** Delay before attempting a reconnection after a socket closure (5s) */
    private readonly RECONNECT_DELAY = 5000;
    /** Time window to complete a barcode scan before the buffer is cleared (30s) */
    private readonly BUFFER_TIMEOUT = 30000;

    /** Active connections indexed by IP address */
    private connections = new Map<string, PrinterConnection>();
    /** Observable stream for connection state updates */
    private statusSubject = new Subject<EposFiscalDevice.ConnectionState>();

    /** Signal to clean up all active subscriptions on service destruction */
    private destroyAll$ = new Subject<void>();

    ngOnDestroy() {
        this.destroyAll$.next();
        this.destroyAll$.complete();
        this.connections.forEach((_, ip) => this.closeScanner(ip));
    }

    /**
     * Updates the status of a specific printer connection.
     */
    private updateStatus(ip: string, status: EposFiscalDevice.ConnectionStatus, error?: string) {
        this.statusSubject.next({ ip, status, error });
    }

    /**
     * Initializes a WebSocket connection to the printer and starts the scanner listener.
     * @param ipAddress The IP address of the Epson printer.
     */
    public async openScanner(ipAddress: string) {
        if (this.connections.has(ipAddress)) {
            return;
        }

        const connection: PrinterConnection = {
            ipAddress,
            buffer: [],
            socket: new WebSocket(`ws://${ipAddress}:8008/connect`),
            shouldReconnect: true,
            destroy$: new Subject<void>(),
            resetBuffer$: new Subject<void>()
        };

        this.connections.set(ipAddress, connection);
        this.setupSocketHandlers(connection);
        this.setupBufferTimeout(connection);
    }

    /**
     * Configures event listeners for the WebSocket (Open, Message, Error, Close).
     */
    private setupSocketHandlers(conn: PrinterConnection) {
        const { socket, ipAddress, destroy$ } = conn;

        this.updateStatus(ipAddress, 'connecting');

        fromEvent(socket, 'open').pipe(
            takeUntil(destroy$)
        ).subscribe(() => {
            this.logEvent(`Connected to ${ipAddress}`);
            this.updateStatus(ipAddress, 'connected');
            this.startHeartbeat(conn);
            // Protocol requirement: tell the printer to activate the local scanner device
            this.sendToSocket(socket, ["open_device", "local_scanner", { type: "type_scanner", crypto: false }]);
        });

        fromEvent<MessageEvent>(socket, 'message').pipe(
            takeUntil(destroy$)
        ).subscribe(event => this.onDataReceived(conn, event.data));

        fromEvent(socket, 'error').pipe(
            takeUntil(destroy$)
        ).subscribe(() => {
            this.logEvent(`Error connecting to ${ipAddress}`);
            this.updateStatus(ipAddress, 'error', 'WebSocket Error')
        });

        fromEvent(socket, 'close').pipe(
            takeUntil(destroy$),
            tap(() => this.updateStatus(ipAddress, 'disconnected')),
            tap(() => this.logEvent(`Connection closed for ${ipAddress}`)),
            delay(this.RECONNECT_DELAY)
        ).subscribe(() => {
            // Automatic reconnection logic if the scanner wasn't closed intentionally
            if (conn.shouldReconnect) {
                this.logEvent(`Reconnecting to ${ipAddress}...`);
                this.cleanupConnection(ipAddress);
                this.openScanner(ipAddress);
            }
        });
    }

    /**
     * Resets the character buffer if no input is received within the timeout period.
     */
    private setupBufferTimeout(conn: PrinterConnection) {
        conn.resetBuffer$.pipe(
            switchMap(() => timer(this.BUFFER_TIMEOUT)),
            takeUntil(conn.destroy$)
        ).subscribe(() => {
            conn.buffer = [];
            this.logEvent(`Buffer cleared for ${conn.ipAddress}`);
        });
    }

    /**
     * Periodically pings the printer to prevent connection timeout.
     */
    private startHeartbeat(conn: PrinterConnection) {
        timer(0, this.HEARTBEAT_INTERVAL).pipe(
            takeUntil(conn.destroy$)
        ).subscribe(() => {
            this.sendToSocket(conn.socket, ["ping"]);
        });
    }

    /**
     * Routes incoming messages based on the Epson WebSocket protocol.
     */
    private onDataReceived(conn: PrinterConnection, rawData: string) {
        try {
            const msg = JSON.parse(rawData);

            // Protocol format: [eventType, deviceName, status, payload]
            if (!Array.isArray(msg)) {
                return;
            }

            const [eventType, , status, payload] = msg;

            switch (eventType) {
                case 'device_data':
                    this.sendAck(conn.socket); // Device data must be acknowledged immediately
                    this.processDeviceData(conn, payload);
                    break;
                case 'open_device':
                case 'close_device':
                    this.logEvent(`Received ${eventType} from ${conn.ipAddress} with status ${status}`);

                    if (status !== 'OK') {
                        this.updateStatus(conn.ipAddress, 'error', `Device ${status}`);
                        conn.socket.close();
                    }
                    break;
            }
        } catch (e) {
            this.logEvent("Parsing error", e);
        }
    }

    /**
     * Converts raw scanner scan-codes into alphanumeric characters.
     * The scanner acts like a keyboard, sending characters one by one.
     */
    private processDeviceData(conn: PrinterConnection, payload: EposFiscalDevice.DeviceDataPayload) {
        if (!payload || payload.type !== 'ondata') {
            if (payload?.type === 'onerror') {
                this.logEvent('Received error from scanner. Closing connection...')
                conn.socket.close();
            }

            return;
        }

        conn.resetBuffer$.next(); // Reset the buffer timeout timer upon activity

        // Epson protocol: data3/4 contains the scan-code, data2 indicates the shift state (uppercase)
        const scanCode = payload.data3 || payload.data4;
        const isUpperCase = payload.data2 === 1;
        const table = isUpperCase ? EposFiscalDevice.scanCodeUpperCaseTable : EposFiscalDevice.scanCodeLowerCaseTable;
        const char = table[scanCode];

        if (typeof char === 'string') {
            conn.buffer.push(char);
        } else if (char === EposFiscalDevice.TENTER && conn.buffer.length) {
            // TENTER represents the 'Enter' key, signaling the end of a barcode scan
            BarcodeManagerService.barcodeInput.next(conn.buffer.join(''));
            conn.buffer = [];
        } else if (char === EposFiscalDevice.TCL) {
            // TCL represents a clear/reset command from the device
            conn.buffer = [];
        }
    }

    /**
     * Closes the connection and stops reconnection attempts.
     */
    public closeScanner(ip: string) {
        const conn = this.connections.get(ip);

        if (!conn) {
            return;
        }

        conn.shouldReconnect = false;

        this.sendToSocket(conn.socket, ["close_device", "local_scanner", {}]);
        this.cleanupConnection(ip);
    }

    /**
     * Disposes of WebSocket resources and clears connection memory.
     */
    private cleanupConnection(ip: string) {
        const conn = this.connections.get(ip);

        if (!conn) {
            return;
        }

        conn.destroy$.next();
        conn.destroy$.complete();
        conn.socket.close();
        this.connections.delete(ip);
    }

    /**
     * Helper to send JSON messages over the WebSocket if it's open.
     */
    private sendToSocket(socket: WebSocket, message: any) {
        if (socket.readyState !== WebSocket.OPEN) {
            return;
        }

        socket.send(JSON.stringify(message));
    }


    private sendAck(socket: WebSocket) {
        this.sendToSocket(socket, ["ack", "dummy"]);
    }

    private logEvent(...args: any[]) {
        console.debug('[ EposFiscalDevice ]', ...args);
    }
}